import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { SyntheticEvent, useMemo } from "react";

import InstantAddToCartProductTileButton from "@/components/Product/ProductTile/components/button/instantAddToCartProductTileButton.container";
import MoreInfoProductTileButton from "@/components/Product/ProductTile/components/button/moreInfoProductTileButton.container";
import ProductTileButtonComponent from "@/components/Product/ProductTile/components/button/productTileButton.component";
import { CartModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { GetStoreStrategy } from "@/services/store.service";
import { getDefaultAddToCartValues } from "@/utils/deliveryMethod";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";
import { isConfigurableProduct } from "@/utils/productConfiguration/productConfiguration.utils";

const getTranslationKeyForVisibilityType = (
    productVisibilityType: ProductVisibilityType
) => {
    switch (productVisibilityType) {
        case ProductVisibilityType.Offline:
        case ProductVisibilityType.NotAvailable:
            return "ecommerce.text.NOT_AVAILABLE";
        case ProductVisibilityType.TemporaryNotAvailable:
            return "ecommerce.text.TEMPORARY_NOT_AVAILABLE";
        case ProductVisibilityType.OnlyAvailableInStore:
            return "ecommerce.text.ONLY_AVAILABLE_IN_STORE";
        default:
            return null;
    }
};

type ProductTileButtonProps = {
    allowInstantAddToCart: boolean;
    product: Product<ProductFieldSet.ProductTile>;
    cart?: CartModel | null;
    extraOnClick?: (e: SyntheticEvent<HTMLButtonElement>) => void;
    className?: string;
};

const ProductTileButton = ({
    allowInstantAddToCart,
    product,
    cart,
    extraOnClick,
    className
}: ProductTileButtonProps) => {
    const { t } = useGranite();

    const storeCode = useStoreCode({ strategy: GetStoreStrategy.Stock });
    const isClientSide = useIsClientSide();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const { defaultDeliveryMethod, defaultQuantity } = useMemo(() => {
        if (!allowInstantAddToCart) {
            return { defaultDeliveryMethod: null, defaultQuantity: null };
        }

        return getDefaultAddToCartValues(product, storeCode);
    }, [allowInstantAddToCart, storeCode]);

    const handleOnClick = (e: SyntheticEvent<HTMLButtonElement>) => {
        if (extraOnClick) {
            extraOnClick(e);
        }
    };

    const notAvailableTranslationKey = getTranslationKeyForVisibilityType(
        product.productVisibilityType
    );

    if (notAvailableTranslationKey) {
        return (
            <ProductTileButtonComponent
                className={className}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                showOutline={true}
            >
                {t(notAvailableTranslationKey)}
            </ProductTileButtonComponent>
        );
    }

    if (
        isClientSide &&
        defaultDeliveryMethod &&
        defaultQuantity &&
        !isConfigurableProduct({ sku: product.sku }) &&
        !product.isAdditionalService
    ) {
        return (
            <InstantAddToCartProductTileButton
                className={className}
                product={product}
                cart={cart}
                extraOnClick={handleOnClick}
                defaultDeliveryMethod={defaultDeliveryMethod}
                defaultQuantity={defaultQuantity}
                preferredStore={storeCode}
            />
        );
    }

    return (
        <MoreInfoProductTileButton
            className={className}
            onClick={handleOnClick}
        />
    );
};

export default ProductTileButton;
